import React, { useState, useEffect } from 'react';
import { 
  Crown, 
  Check, 
  X, 
  BarChart3, 
  Palette, 
  Shield,
  Trophy,
  ChevronDown,
  ChevronUp,
  Gift
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import GiftCardModal from '../components/GiftCardModal';
import AtlosPayButton from '../components/AtlosPayButton';
import { YEARLY_MONTHLY_EQUIVALENT, getPremiumPlanPrice } from '../config/pricing';

interface Feature {
  name: string;
  included: boolean;
}

interface FAQ {
  question: string;
  answer: string;
  isOpen: boolean;
}

type PremiumPlan = 'monthly' | 'yearly' | 'lifetime';

interface PricingCardProps {
  title: string;
  price: string;
  period: string;
  features: Feature[];
  isPopular?: boolean;
  isPremium?: boolean;
  planType?: PremiumPlan;
  selectedPlan: 'monthly' | 'yearly';
  userIsPremium: boolean;
  onUpgrade: (plan: PremiumPlan) => void;
  getAmount: (plan: PremiumPlan) => number;
}

const PricingCard: React.FC<PricingCardProps> = ({ 
  title, 
  price, 
  period, 
  features, 
  isPopular = false,
  isPremium = false,
  planType = 'monthly',
  selectedPlan,
  userIsPremium,
  onUpgrade,
  getAmount
}) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className={`relative bg-cardBg rounded-xl p-6 shadow-lg border border-secondary ${isPopular ? 'ring-2 ring-accent' : ''}`}
    whileHover={{ y: -5, transition: { duration: 0.2 } }}
  >
    {isPopular && (
      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
        <span className="bg-accent text-primary px-4 py-1 rounded-full text-sm font-medium">
          Most Popular
        </span>
      </div>
    )}
    
    <div className="text-center mb-6">
      <h3 className="text-xl font-bold mb-2 flex items-center justify-center">
        {isPremium && <Crown className="w-5 h-5 mr-2 text-accent" />}
        {title}
      </h3>
      <div className="text-3xl font-bold text-accent">
        {price}
        <span className="text-lg text-textMuted font-normal">/{period}</span>
      </div>
      {isPremium && selectedPlan === 'yearly' && title !== "Lifetime Pro" && (
        <div className="mt-2 text-green-500 text-sm font-medium">
          Save 20% with yearly billing
        </div>
      )}
    </div>
    
    <div className="space-y-3 mb-6">
      {features.map((feature: Feature, featureIndex: number) => (
        <motion.div 
          key={feature.name}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: featureIndex * 0.05 }}
          className="flex items-center space-x-3"
        >
          {feature.included ? (
            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
          ) : (
            <X className="w-5 h-5 text-textMuted flex-shrink-0" />
          )}
          <span className={feature.included ? 'text-text' : 'text-textMuted line-through'}>
            {feature.name}
          </span>
        </motion.div>
      ))}
    </div>

    {(() => {
      if (isPremium) {
        return (
          <AtlosPayButton
            planType={title === "Lifetime Pro" ? 'lifetime' : selectedPlan}
            amount={getAmount(title === "Lifetime Pro" ? 'lifetime' : selectedPlan)}
            onPaymentStarted={() => onUpgrade(title === "Lifetime Pro" ? 'lifetime' : selectedPlan)}
            className={`w-full ${title === "Lifetime Pro" ? "bg-purple-600 hover:bg-purple-700" : "bg-accent hover:bg-yellow-500"} text-primary font-semibold py-3 rounded-lg transition-all duration-200`}
          >
            {title === "Lifetime Pro" ? "Get Lifetime Access" : "Upgrade to Premium"}
          </AtlosPayButton>
        );
      }
      
      if (userIsPremium) {
        return (
          <button className="w-full bg-secondary text-textMuted font-medium py-3 rounded-lg cursor-not-allowed">
            Current Plan
          </button>
        );
      }
      
      return (
        <Link to="/typing" className="block w-full">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-full bg-secondary hover:bg-opacity-70 text-text font-medium py-3 rounded-lg transition-all duration-200"
          >
            Continue with Free
          </motion.button>
        </Link>
      );
    })()}
  </motion.div>
);

const Premium: React.FC = () => {
  const { state: userState } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('monthly');
  const [showGiftCardModal, setShowGiftCardModal] = useState(false);
  const [completedGiftOrderId, setCompletedGiftOrderId] = useState<string | null>(null);
  
  // Check for payment success/failure in URL parameters
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const plan = searchParams.get('plan');
    
    // Handle gift card payments
    const giftcardSuccess = searchParams.get('giftcard_success');
    const giftcardCancelled = searchParams.get('giftcard_cancelled');
    const giftCardOrderId = searchParams.get('order_id');
    
    if (success === 'true' && plan) {
      toast.success(`Payment received for ${plan}. Premium activation will be applied after verification.`);
      setSearchParams({});
    } else if (cancelled === 'true') {
      toast.error('Payment was cancelled. You can try again anytime.');
      setSearchParams({});
    } else if (giftcardSuccess === 'true' && giftCardOrderId) {
      setCompletedGiftOrderId(giftCardOrderId);
      setShowGiftCardModal(true);
      toast.success('Payment confirmed. Loading your gift card codes...');
      setSearchParams({});
    } else if (giftcardCancelled === 'true') {
      toast.error('Gift card purchase was cancelled.');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  const [faqs, setFaqs] = useState<FAQ[]>([
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Our subscriptions are non-refundable and cannot be canceled mid-term. When you subscribe, you commit to the full billing cycle. Your subscription will automatically renew unless you turn off auto-renewal in your account settings.",
      isOpen: false
    },
    {
      question: "Is there a free trial?",
      answer: "We currently do not offer any free trials for our Premium subscriptions. You can try all the basic features with our free plan before upgrading.",
      isOpen: false
    },
    {
      question: "What payment methods do you accept?",
      answer: "We currently only accept USDC payments on the Polygon network through our secure payment provider, Atlos.",
      isOpen: false
    },
    {
      question: "Do you offer student discounts?",
      answer: "We currently do not offer any student discounts. Our pricing is already set competitively to provide great value for all users.",
      isOpen: false
    },
    {
      question: "Can I switch between monthly and yearly plans?",
      answer: "Yes, you can switch between plans at any time. If you switch from monthly to yearly, you'll immediately be charged the yearly rate and receive the discount. If you switch from yearly to monthly, the change will take effect at the end of your current billing cycle.",
      isOpen: false
    }
  ]);

  const toggleFAQ = (index: number) => {
    setFaqs(faqs.map((faq, i) => 
      i === index ? { ...faq, isOpen: !faq.isOpen } : { ...faq, isOpen: false }
    ));
  };

  const features: { free: Feature[]; premium: Feature[] } = {
    free: [
      { name: 'All typing modes', included: true },
      { name: 'Basic statistics', included: true },
      { name: 'Leaderboards', included: true },
      { name: '1v1 Battles (limited)', included: true },
      { name: 'Basic themes', included: true },
      { name: 'Advanced analytics', included: false },
      { name: 'Typing heatmap', included: false },
      { name: 'Unlimited battles', included: false },
      { name: 'Premium themes', included: false },
      { name: 'Custom fonts', included: false },
      { name: 'Progress tracking', included: false },
      { name: 'Code vs Word comparison', included: false },
      { name: 'Ad-free experience', included: false },
    ],
    premium: [
      { name: 'All typing modes', included: true },
      { name: 'Basic statistics', included: true },
      { name: 'Leaderboards', included: true },
      { name: 'Unlimited 1v1 battles', included: true },
      { name: 'All themes & fonts', included: true },
      { name: 'Advanced analytics', included: true },
      { name: 'Typing heatmap', included: true },
      { name: 'Private battle rooms', included: true },
      { name: 'Premium themes', included: true },
      { name: 'Custom fonts & effects', included: true },
      { name: 'Detailed progress tracking', included: true },
      { name: 'Code vs Word comparison', included: true },
      { name: 'Completely ad-free', included: true },
    ],
  };

  const premiumBenefits = [
    {
      icon: BarChart3,
      title: 'Advanced Analytics',
      description: 'Detailed typing heatmaps, error analysis, and progress tracking to help you improve faster.',
      color: 'text-blue-500',
    },
    {
      icon: Palette,
      title: 'Premium Themes & Fonts',
      description: 'Unlock exclusive themes like Matrix, Neon, and custom fonts including Hacker and Pixel styles.',
      color: 'text-purple-500',
    },
    {
      icon: Trophy,
      title: 'Unlimited Battles',
      description: 'Create private rooms, unlimited 1v1 battles, and access to VIP leaderboards.',
      color: 'text-yellow-500',
    },
    {
      icon: Shield,
      title: 'Ad-Free Experience',
      description: 'Enjoy TypeRush without any distractions. Focus purely on improving your typing skills.',
      color: 'text-green-500',
    },
  ];

  const handleUpgrade = (plan: PremiumPlan = 'monthly') => {
    if (!userState.isAuthenticated) {
      toast.error('Please log in to upgrade to Premium');
      return;
    }
    
    // Success will be handled by URL parameters after payment
    toast.success('Redirecting to secure payment...');
  };

  const getAmount = (plan: 'monthly' | 'yearly' | 'lifetime') => {
    return getPremiumPlanPrice(plan);
  };

  return (
    <div className="max-w-6xl mx-auto pb-20">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl font-bold mb-4 flex items-center justify-center">
          <Crown className="w-10 h-10 mr-3 text-accent" />
          TypeRush Premium
        </h1>
        <p className="text-xl text-textMuted max-w-2xl mx-auto">
          Unlock advanced features and take your typing skills to the next level
        </p>
      </motion.div>

      {/* Premium Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {premiumBenefits.map((benefit, benefitIndex) => {
          const Icon = benefit.icon;
          return (
            <motion.div 
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: benefitIndex * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="bg-cardBg border border-secondary rounded-xl p-6 shadow-md"
            >
              <div className="flex items-start space-x-4">
                <div className={`p-3 rounded-lg bg-secondary ${benefit.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-textMuted">{benefit.description}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Pricing Toggle */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
        className="flex justify-center mb-8"
      >
        <div className="bg-cardBg border border-secondary rounded-lg p-1 flex shadow-md">
          <motion.button
            onClick={() => setSelectedPlan('monthly')}
            className={`px-4 py-2 rounded-md transition-all ${
              selectedPlan === 'monthly'
                ? 'bg-accent text-primary font-medium'
                : 'text-textMuted hover:text-accent'
            }`}
            whileHover={selectedPlan === 'monthly' ? {} : { scale: 1.05 }}
            whileTap={selectedPlan === 'monthly' ? {} : { scale: 0.95 }}
          >
            Monthly
          </motion.button>
          <div className="relative">
            <motion.button
              onClick={() => setSelectedPlan('yearly')}
              className={`px-4 py-2 rounded-md transition-all relative ${
                selectedPlan === 'yearly'
                  ? 'bg-accent text-primary font-medium'
                  : 'text-textMuted hover:text-accent'
              }`}
              whileHover={selectedPlan === 'yearly' ? {} : { scale: 1.05 }}
              whileTap={selectedPlan === 'yearly' ? {} : { scale: 0.95 }}
            >
              Yearly
            </motion.button>
            <motion.span 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full"
            >
              Save 20%
            </motion.span>
          </div>
        </div>
      </motion.div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
        <PricingCard
          title="Basic"
          price="Free"
          period="forever"
          features={features.free}
          selectedPlan={selectedPlan}
          userIsPremium={Boolean(userState.user?.isPremium)}
          onUpgrade={handleUpgrade}
          getAmount={getAmount}
        />
        
        <PricingCard
          title="Pro Typer"
          price={
            selectedPlan === 'monthly' 
              ? `$${getPremiumPlanPrice('monthly').toFixed(2)}`
              : `$${YEARLY_MONTHLY_EQUIVALENT.toFixed(2)}`
          }
          period="month"
          features={features.premium}
          isPopular={true}
          isPremium={true}
          selectedPlan={selectedPlan}
          userIsPremium={Boolean(userState.user?.isPremium)}
          onUpgrade={handleUpgrade}
          getAmount={getAmount}
        />
        
        <PricingCard
          title="Lifetime Pro"
          price={`$${getPremiumPlanPrice('lifetime').toFixed(2)}`}
          period="one-time"
          features={[
            ...features.premium,
            { name: 'Ember exclusive theme', included: true },
            { name: 'Future premium features', included: true },
            { name: 'No recurring payments', included: true }
          ]}
          isPremium={true}
          selectedPlan={selectedPlan}
          userIsPremium={Boolean(userState.user?.isPremium)}
          onUpgrade={handleUpgrade}
          getAmount={getAmount}
        />
      </div>

      {/* FAQ */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="bg-cardBg border border-secondary rounded-xl p-8 shadow-md"
      >
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        
        <div className="space-y-4 max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div 
              key={faq.question}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="border border-secondary rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full text-left p-4 bg-primary hover:bg-secondary transition-colors flex justify-between items-center"
              >
                <h3 className="font-semibold">{faq.question}</h3>
                {faq.isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              
              <AnimatePresence>
                {faq.isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <p className="p-4 text-textMuted border-t border-secondary">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Gift Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        whileHover={{ y: -5 }}
        className="mt-12 bg-gradient-to-r from-accent via-yellow-500 to-accent p-1 rounded-xl shadow-lg"
      >
        <div className="bg-cardBg rounded-lg p-6 flex flex-col md:flex-row items-center justify-between">
          <div className="mb-4 md:mb-0">
            <h3 className="text-xl font-bold mb-2 flex items-center">
              <Gift className="w-5 h-5 mr-2 text-accent" />
              Give the Gift of Premium
            </h3>
            <p className="text-textMuted">
              Share TypeRush Premium with friends and help them improve their typing skills!
            </p>
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-accent hover:bg-yellow-500 text-primary font-semibold px-6 py-3 rounded-lg transition-all duration-200"
            onClick={() => {
              if (!userState.isAuthenticated) {
                toast.error('Please log in to access gift cards');
                return;
              }
              setShowGiftCardModal(true);
            }}
          >
            Get Gift Card
          </motion.button>
        </div>
      </motion.div>

      {/* Gift Card Modal */}
      <GiftCardModal 
        isOpen={showGiftCardModal} 
        onClose={() => {
          setShowGiftCardModal(false);
          setCompletedGiftOrderId(null);
        }} 
        completedOrderId={completedGiftOrderId}
      />

    </div>
  );
};

export default Premium;
